import React, { useState, useEffect } from "react";
import { API_URL } from "../../config";
import styles from "./userDetails.module.css";

// Fallback if the API is unreachable — the backend list is the source of truth
const FALLBACK_FEES = {
  Ikeja: 2500,
  Surulere: 4500,
  Yaba: 4000,
  Lekki: 7000,
  "Victoria Island": 5500,
};

const STORAGE_KEY = "lormami_checkout_details";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const UserDetailsModal = ({ show, onHide, onConfirm }) => {
  const [fees, setFees] = useState(FALLBACK_FEES);
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Delivery fees come from the backend so the fee shown here is exactly
  // what checkout charges.
  useEffect(() => {
    fetch(`${API_URL}/orders/delivery-fees`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setFees(data))
      .catch(() => {});
  }, []);

  // Returning customers get their details pre-filled
  useEffect(() => {
    if (!show) return;
    const saved = loadSaved();
    if (saved.fullName) setFullName(saved.fullName);
    if (saved.address) setAddress(saved.address);
    if (saved.location) setLocation(saved.location);
    if (saved.email) setEmail(saved.email);
    if (saved.phone) setPhone(saved.phone);
  }, [show]);

  if (!show) return null;

  const fee = fees[location] || 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    const details = {
      fullName: fullName.trim(),
      address: address.trim(),
      location,
      email: email.trim(),
      phone: phone.trim(),
      fee,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(details));
    } catch {
      // private browsing — fine, just don't remember
    }
    onConfirm(details);
  };

  return (
    <div className={styles.overlay} onClick={onHide}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div>
            <span className={styles.eyebrow}>Checkout</span>
            <h2 className={styles.title}>Delivery Details</h2>
          </div>
          <button type="button" className={styles.close} onClick={onHide} aria-label="Close">
            ×
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="cd-name">Full name</label>
            <input
              id="cd-name"
              className={styles.input}
              required
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cd-phone">Phone number</label>
            <input
              id="cd-phone"
              className={styles.input}
              type="tel"
              required
              autoComplete="tel"
              placeholder="0810 000 0000"
              pattern="[0-9+ ]{7,15}"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cd-email">Email</label>
            <input
              id="cd-email"
              className={styles.input}
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cd-address">Delivery address</label>
            <input
              id="cd-address"
              className={styles.input}
              required
              autoComplete="street-address"
              placeholder="Street, number, landmark"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cd-lga">Area (Local Government)</label>
            <select
              id="cd-lga"
              className={styles.select}
              required
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              <option value="" disabled>
                Select your area
              </option>
              {Object.keys(fees)
                .sort()
                .map((lga) => (
                  <option key={lga} value={lga}>
                    {lga} — ₦{fees[lga].toLocaleString("en-NG")}
                  </option>
                ))}
            </select>
          </div>

          {location && (
            <div className={styles.feeNote}>
              <span>Delivery to {location}</span>
              <strong>₦{fee.toLocaleString("en-NG")}</strong>
            </div>
          )}

          <button type="submit" className={styles.submit}>
            Continue to Payment
          </button>
          <p className={styles.secure}>Payments are processed securely by Paystack.</p>
        </form>
      </div>
    </div>
  );
};

export default UserDetailsModal;
